import React, { useRef, useEffect, useState, useCallback } from 'react';
import Lightpick from 'lightpick';
import 'lightpick/css/lightpick.css';
import moment from 'moment';

// Matches lightpick's own `format` and `separator` defaults, so a range the
// calendar writes and a range typed by hand end up as the same string.
const OUTPUT_FORMAT = 'YYYY-MM-DD HH:mm:ss';
const SEPARATOR = ' - ';
const DAY_START = '00:00:00';
const DAY_END = '23:59:59';

// Two dates, each with an optional time, around a dash. The dash is anchored
// between two complete dates so the ones inside YYYY-MM-DD are not mistaken
// for the separator, and the surrounding spaces are optional.
const RANGE_PATTERN = new RegExp(
  '^\\s*(\\d{4}-\\d{1,2}-\\d{1,2}(?:[ T]\\d{1,2}:\\d{2}(?::\\d{2})?)?)' +
  '\\s*-\\s*' +
  '(\\d{4}-\\d{1,2}-\\d{1,2}(?:[ T]\\d{1,2}:\\d{2}(?::\\d{2})?)?)\\s*$'
);

// Single-digit months, days and hours are accepted so a fast typist is not
// forced to zero-pad; everything is normalised to OUTPUT_FORMAT on commit.
const INPUT_FORMATS = [
  'YYYY-M-D HH:mm:ss', 'YYYY-M-D H:mm:ss',
  'YYYY-M-D HH:mm', 'YYYY-M-D H:mm',
];

const parseSide = (raw, fallbackTime) => {
  const text = raw.trim().replace('T', ' ');
  // A bare date means the whole day, matching what the calendar produces.
  const withTime = /\d{1,2}:\d{2}/.test(text) ? text : `${text} ${fallbackTime}`;
  const parsed = moment(withTime, INPUT_FORMATS, true);
  return parsed.isValid() ? parsed : null;
};

const parseRange = (text) => {
  const match = String(text || '').match(RANGE_PATTERN);
  if (!match) return null;

  const startDate = parseSide(match[1], DAY_START);
  const endDate = parseSide(match[2], DAY_END);
  // A backwards range is a typo, not a promotion period.
  if (!startDate || !endDate || endDate.isBefore(startDate)) return null;

  return {
    startDate,
    endDate,
    value: startDate.format(OUTPUT_FORMAT) + SEPARATOR + endDate.format(OUTPUT_FORMAT),
  };
};

const LightPickerInput = ({
  value,
  onChange,
  renderInput,
  ...inputProps
}) => {
  const inputRef = useRef();
  const pickerRef = useRef(null);
  const focusedRef = useRef(false);
  // Held in a ref so an inline arrow from the parent does not tear down and
  // rebuild the calendar on every keystroke.
  const onChangeRef = useRef(onChange);
  const [text, setText] = useState(value || '');
  const [invalid, setInvalid] = useState(false);

  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  const emit = useCallback((range) => {
    if (onChangeRef.current) onChangeRef.current(range);
  }, []);

  useEffect(() => {
    // Only run in browser environment
    if (typeof window === 'undefined' || !inputRef.current) return undefined;

    const lightpick = new Lightpick({
      field: inputRef.current,
      singleDate: false,
      format: OUTPUT_FORMAT,
      numberOfMonths: 2,
      numberOfColumns: 2,
      selectForward: true,
      autoclose: false,
      onSelect: function(start, end) {
        if (!start || !end) return;
        // Format with time (defaulting to 00:00:00 for start and 23:59:59 for end)
        const startDate = `${moment(start).format('YYYY-MM-DD')} ${DAY_START}`;
        const endDate = `${moment(end).format('YYYY-MM-DD')} ${DAY_END}`;
        const val = startDate + SEPARATOR + endDate;

        // lightpick writes the field directly; mirroring it into state keeps
        // the controlled input from fighting it back to the previous text.
        setText(val);
        setInvalid(false);
        emit({
          startDate: moment(startDate, OUTPUT_FORMAT),
          endDate: moment(endDate, OUTPUT_FORMAT),
          value: val,
        });
      }
    });

    pickerRef.current = lightpick;

    return () => {
      lightpick.destroy();
      pickerRef.current = null;
    };
  }, [emit]);

  // The picker reads its field only at construction, so a committed range has
  // to be pushed in for the calendar to open on it. Called on commit rather
  // than per keystroke: setStartDate blanks the field for an unparseable date,
  // and rewriting the field mid-word would send the caret to the end.
  const syncPicker = useCallback((nextText) => {
    const picker = pickerRef.current;
    if (!picker) return;

    const range = parseRange(nextText);
    picker.setDateRange(
      range ? range.startDate.toDate() : null,
      range ? range.endDate.toDate() : null,
      true
    );
    // setStartDate/setEndDate assign to the DOM node behind React's back, and
    // React will not repaint a value it thinks is unchanged — restore it here.
    if (inputRef.current && inputRef.current.value !== nextText) {
      inputRef.current.value = nextText;
    }
  }, []);

  useEffect(() => {
    // Mid-edit text belongs to the user until they commit it, so an incoming
    // value is only adopted while the field is unfocused.
    if (focusedRef.current) return;
    const next = value || '';
    setText(next);
    setInvalid(false);
    syncPicker(next);
  }, [value, syncPicker]);

  const commit = useCallback((raw) => {
    const trimmed = String(raw || '').trim();

    if (!trimmed) {
      setText('');
      setInvalid(false);
      syncPicker('');
      emit({ startDate: null, endDate: null, value: '' });
      return;
    }

    const range = parseRange(trimmed);
    if (!range) {
      // Half-typed text is never sent upstream — it would be saved as the
      // promotion period. The last committed value stands instead.
      setText(value || '');
      setInvalid(false);
      syncPicker(value || '');
      return;
    }

    setText(range.value);
    setInvalid(false);
    syncPicker(range.value);
    emit(range);
  }, [emit, syncPicker, value]);

  const handleType = (event) => {
    const next = event.target.value;
    setText(next);
    setInvalid(next.trim() !== '' && !parseRange(next));
  };

  const handleFocus = () => { focusedRef.current = true; };

  const handleBlur = (event) => {
    focusedRef.current = false;
    commit(event.target.value);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      // The field sits inside a form; committing must not submit it.
      event.preventDefault();
      commit(event.target.value);
    }
  };

  const typingProps = {
    value: text,
    onChange: handleType,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onKeyDown: handleKeyDown,
    title: inputProps.title || `Pick a range, or type one: 2026-09-01${SEPARATOR}2026-09-30`,
  };

  const defaultInput = (
    <input
      ref={inputRef}
      type="text"
      className="form-control"
      placeholder={inputProps.placeholder || "Select date & time range"}
      {...inputProps}
      {...typingProps}
      style={{
        width: 300,
        fontSize: '12px',
        height: '38px',
        ...(inputProps.style || {}),
        ...(invalid ? { borderColor: '#dc3545' } : {}),
      }}
    />
  );

  return renderInput
    ? renderInput({ inputProps: { ...inputProps, ...typingProps }, ref: inputRef })
    : defaultInput;
};

export default LightPickerInput;
